import React from "react";
import { Formik } from "formik";
import _ from "lodash";
import { useMappedState } from "redux-react-hook";

import { FieldMultiSelect } from "src/library/components/Forms";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import {
  IUserGroup,
  useUserApi
} from "src/core/users/api/users.api";
import { IStore } from "src/core/shared/store";

interface UserGroupMultiSelect {
  onUserGroupsChange: (data: { selected: string, userGroupsSelected: string[], fullUserGroupsSelected: IUserGroup[]; }) => void;
  userGroupFilters?: FilterParams<IUserGroup>;
  userGroupPagination?: PaginatedParams<IUserGroup>;
  initialValues?: number[];
  debounceTime?: number;
  singleSelection?: boolean;
  labelOverride?: string;
}

const defaultPagination: PaginatedParams<IUserGroup> = {
  limit: 100,
  offset: 0,
  page: 0,
  sort: [ [ "name", "ASC" ] ]
};

export const UserGroupMultiSelect: React.ReactFCWithChildren<UserGroupMultiSelect> = ({
  onUserGroupsChange, userGroupFilters, initialValues, userGroupPagination, debounceTime, singleSelection, labelOverride
}) => {
  const [ recipientUserGroupState, setRecipientUserGroupState ] = React.useState<Array<{ text: string; value: string }>>([]);
  const [ loadedUserGroups, setLoadedUserGroups ] = React.useState<boolean>(false);
  const [ userGroupsResponse, getUserGroups ] = useUserApi("LIST_USER_GROUPS");
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings && global.settings.terminology }));

  // On mount, get the user groups
  React.useEffect(() => {
    if (!loadedUserGroups) {
      getUserGroups({
        pagination: userGroupPagination || defaultPagination,
        filters: userGroupFilters || {}
      });
    }
  }, [
    getUserGroups,
    loadedUserGroups,
    userGroupFilters,
    userGroupPagination
  ]);

  // Set list of user groups as group options.
  React.useEffect(() => {
    if (userGroupsResponse.data && userGroupsResponse.data.result) {
      const userGroupValues = userGroupsResponse.data.result.map(userGroup => ({
        text: userGroup.name,
        value: userGroup.id.toString()
      }));

      //populate data with all the new data ensuring uniqueness
      setRecipientUserGroupState(prev => {
        const merged = [ ...prev, ...userGroupValues ];

        //hash maps do not allow duplicate keys
        return Array.from(new Map(merged.map(option => [ option.value, option ])).values());
      });
      setLoadedUserGroups(true);
    }
  }, [ userGroupsResponse ]);

  //for passing into the multiSelect
  const handleSpecificSearch = React.useCallback((search: string | null) => {
    if (search) {
      //get fuzzy searched drops
      getUserGroups({
        pagination: {
          ...defaultPagination,
          limit: 50
        },
        filters: { name: { $like: `%${search}%` } }
      });
    } else {
      getUserGroups({
        pagination: userGroupPagination || defaultPagination,
        filters: userGroupFilters || {}
      });
    }
  }, [
    getUserGroups,
    userGroupFilters,
    userGroupPagination
  ]);

  const label = React.useMemo(() => {
    if (!singleSelection) {
      return `Select ${terminology ? terminology.user.singular.text : "User"} Groups`;
    } else {
      return `Select ${terminology ? terminology.user.singular.text : "User"} Group`;
    }
  }, [ singleSelection, terminology ]);

  return (
    <Formik
      initialValues={{ userGroupIds: initialValues?.map(i => i.toString()) }}
      // dont submit as should be used in from above this
      onSubmit={() => null}
    >
      {({ values, handleChange }) => (
        <FieldMultiSelect
          values={values.userGroupIds}
          name="recipientMultipleUserGroups"
          handleChange={handleChange}
          label={labelOverride ? labelOverride : label}
          options={recipientUserGroupState}
          loading={userGroupsResponse.pending}
          validateOnChange={false}
          placeholder="Start typing to search user groups..."
          fieldLogic={(affected, oldSelected) => onUserGroupsChange({
            selected: affected,
            userGroupsSelected: oldSelected,
            fullUserGroupsSelected: _.compact(_.map(oldSelected, selected => _.find(userGroupsResponse.data?.result, c => c.id.toString() === selected)))
          })}
          handleSpecificSearch={handleSpecificSearch}
          debounceTime={debounceTime}
          singleSelection={singleSelection}
        />
      )}
    </Formik>
  );
};
